import React, { useState } from 'react';
import { ActionType, GetInstructionsApiResponseModel, IntegrationType } from '../services/api/types';
import ActionInstructions from './ActionInstructions';
import InstructionsFooter from './InstructionsFooter';
import { P2GBox } from '@parcel2go/peacock-legacy-core/dist/box';
import { P2GGrid } from '@parcel2go/peacock-legacy-core/dist/grid';
import { P2GImage } from '@parcel2go/peacock-legacy-core/dist/image';
import { P2GDivider } from '@parcel2go/peacock-legacy-core/dist/divider';
import { Settings } from '../settings';
import { P2GSurface } from '@parcel2go/peacock-legacy-layout/dist/surface';
import { P2GText } from '@parcel2go/peacock-legacy-core/dist/typography';
import { P2GLinkGroup } from '@parcel2go/peacock-legacy-core/dist/linkgroup';
import { P2GButton } from '@parcel2go/peacock-legacy-core/dist/button';
import { P2GLink } from '@parcel2go/peacock-legacy-core/dist/link';

interface props {
  integrationSlug: string;
  integrationType: IntegrationType;
  action: ActionType;
  onError: () => void;
  onComplete: (accountId: string, followUpAction?: ActionType) => void;
  isXs: boolean;
  onCancel: () => void;
  accountCount: number;
}

const NoDistractionAction: React.FC<props> = (props) => {
  const [instructions, setInstructions] = useState<GetInstructionsApiResponseModel>(null);
  const [initialAccountCount] = useState(props.accountCount || 0);
  const [accountId, setAccountId] = useState<string>();

  const onComplete = (accountId: string) => setAccountId(accountId);
  const onShowAll = () => props.onComplete(accountId);

  return (
    <P2GSurface>
      <P2GBox p={props.isXs ? 0 : 3}>
        <P2GGrid container spacing={5} alignItems="center">
          <P2GGrid item md={6} xs={12}>
            <P2GImage
              src={`https://content.parcelsolutions.net/42c5534b-0f47-4342-80f0-8f21203f0669/integrations/${props.integrationType}/${props.integrationSlug}.svg`.toLowerCase()}
              style={{ width: props.isXs ? '80%' : '100%' }}
              alt={props.integrationSlug}
            />
          </P2GGrid>
          <P2GGrid item md={6} xs={12}>
            {accountId ? (
              <>
                <P2GGrid container direction="column" spacing={3}>
                  <P2GGrid item>
                    <P2GText variant="h2">All Done!</P2GText>
                    <P2GText component="p">
                      {initialAccountCount == 0
                        ? 'You have successfully linked your account.'
                        : 'You have successfully linked another account.'}
                    </P2GText>
                  </P2GGrid>
                  <P2GGrid item>
                    {initialAccountCount == 0 ? (
                      <P2GText component="p">
                        As this is your first account link, you have a few options. You can go to{' '}
                        <P2GLink href={Settings.settings.links.SmartSend}>Smart Send</P2GLink> and see your recently
                        sold items or head over to{' '}
                        <P2GLink href={Settings.settings.links.RuleManager}>Rule Manager</P2GLink> to set up some basic
                        rules and make sending quicker and easier.
                      </P2GText>
                    ) : (
                      <P2GText component="p">
                        Continue to <P2GLink href={Settings.settings.links.SmartSend}>Smart Send</P2GLink> and see your
                        recently sold items or, if you haven't already, head over to{' '}
                        <P2GLink href={Settings.settings.links.RuleManager}>Rule Manager</P2GLink> to set up some basic
                        rules and make sending quicker and easier.
                      </P2GText>
                    )}
                  </P2GGrid>
                  <P2GGrid item>
                    {Settings.settings.links.SmartSend && (
                      <P2GButton href={Settings.settings.links.SmartSend}>Go to Smart Send</P2GButton>
                    )}
                    <P2GLinkGroup
                      pt={2}
                      links={[
                        <P2GLink onClick={onShowAll}>Add Another Account</P2GLink>,
                        Settings.settings.links.RuleManager && (
                          <P2GLink href={Settings.settings.links.RuleManager}>Go to Rule Manager</P2GLink>
                        ),
                      ]}
                    />
                  </P2GGrid>
                </P2GGrid>
              </>
            ) : (
              <ActionInstructions
                configRequired={false}
                action={props.action}
                integrationSlug={props.integrationSlug}
                integrationType={props.integrationType}
                onComplete={onComplete}
                accountId={null}
                onError={props.onError}
                onInstructionsLoaded={setInstructions}
                hideFooter={true}
              />
            )}
          </P2GGrid>
          {instructions && (
            <>
              <P2GGrid item xs={12}>
                <P2GDivider />
              </P2GGrid>
              <P2GGrid item xs={12}>
                <InstructionsFooter children={instructions.footerText} />
              </P2GGrid>
            </>
          )}
        </P2GGrid>
      </P2GBox>
    </P2GSurface>
  );
};
export default NoDistractionAction;
